import type { SensorData } from './analysisTabSensorTypes';

function normalizeParameterUnit(parameter: { units?: unknown[] }): string {
  if (!parameter.units || parameter.units.length === 0) {
    return '';
  }
  const firstUnit = parameter.units[0];
  if (typeof firstUnit === 'string') {
    return firstUnit.trim();
  }
  if (firstUnit && typeof firstUnit === 'object') {
    const unitRecord = firstUnit as { code?: string; description?: string };
    const unit = unitRecord.code || unitRecord.description || JSON.stringify(firstUnit);
    return unit.trim();
  }
  const unit = String(firstUnit).trim();
  if (!unit || unit === 'undefined' || unit === 'null') {
    return '';
  }
  return unit;
}

function hasSensorIdentity(parameter: { sensorName?: string; channelId?: string; tag?: string }): boolean {
  return Boolean(parameter.sensorName && (parameter.channelId || parameter.tag));
}

function sensorAlreadyExists(
  allSensors: SensorData[],
  channelId: string,
  sensorName: string,
  assetTypeName: string
): boolean {
  for (const sensor of allSensors) {
    if (
      sensor.channelId === channelId &&
      sensor.sensorName === sensorName &&
      sensor.assetType === assetTypeName
    ) {
      return true;
    }
  }
  return false;
}

export function buildSensorsFromAssetTypesResponse(
  assetTypesResponse: unknown,
  colorPalette: string[],
  assetTypeFilter?: string
): SensorData[] {
  const assetTypes = (assetTypesResponse as { Data?: unknown })?.Data || assetTypesResponse;
  if (!Array.isArray(assetTypes)) {
    return [];
  }

  const allSensors: SensorData[] = [];
  let colorIndex = 0;

  for (const assetType of assetTypes) {
    const parameters = assetType?.parameters;
    if (!Array.isArray(parameters)) {
      continue;
    }

    const typeName = assetType.typeName as string;
    for (const parameter of parameters) {
      if (!hasSensorIdentity(parameter)) {
        continue;
      }

      const channelId = parameter.channelId || parameter.tag;
      const unit = normalizeParameterUnit(parameter);
      const sensorKey = `${channelId}_${parameter.sensorName}_${typeName}`;
      if (sensorAlreadyExists(allSensors, channelId, parameter.sensorName, typeName)) {
        continue;
      }

      allSensors.push({
        id: sensorKey,
        channelId,
        sensorName: parameter.sensorName,
        unit,
        color: colorPalette[colorIndex % colorPalette.length],
        dataPoints: [],
        assetType: typeName,
      });
      colorIndex += 1;
    }
  }

  if (!assetTypeFilter) {
    return allSensors;
  }
  return allSensors.filter(sensor => sensor.assetType === assetTypeFilter);
}
